/**
 * Reset Password Screen
 *
 * Verifies code and sets new password
 */

import { FormEvent, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface ResetPasswordProps {
  identifier: string;
  destination?: string; // Masked email/phone where code was sent
  onSubmit: (code: string, newPassword: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  error?: string | null;
}

export function ResetPassword({
  identifier,
  destination,
  onSubmit,
  onResendCode,
  error,
}: ResetPasswordProps) {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate password match
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      await onSubmit(code, newPassword);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await onResendCode();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Reset Password
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {destination
          ? `We sent a code to ${destination}`
          : `We sent a code to ${identifier}`}
      </Typography>

      {(error || validationError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || validationError}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Verification Code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        margin="normal"
        required
        autoFocus
        disabled={isLoading}
        inputProps={{ maxLength: 6 }}
      />

      <TextField
        fullWidth
        type={showPassword ? 'text' : 'password'}
        label="New Password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        margin="normal"
        required
        disabled={isLoading}
        helperText="At least 6 characters"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        type={showPassword ? 'text' : 'password'}
        label="Confirm Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        margin="normal"
        required
        disabled={isLoading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || !code || !newPassword || !confirmPassword}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoading ? 'Resetting...' : 'Reset Password'}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={handleResend}
        disabled={isLoading}
      >
        Resend Code
      </Button>
    </Box>
  );
}
